import { Text } from "@chakra-ui/react";
import { useFormikContext } from "formik";
import { FC, useCallback, useEffect } from "react";

import { GraphQLEnums } from "~/src/__generated__/GraphQLEnums";
import { UserRole } from "~/src/__generated__/schema";
import { FormControl } from "~/src/components/common/forms/FormControl";
import { FormikRadioButtonsField } from "~/src/components/common/forms/FormikRadioButtonsField";
import { FormikSelectField } from "~/src/components/common/forms/FormikSelectField";
import { FormikTextField } from "~/src/components/common/forms/FormikTextField";
import { Switch } from "~/src/components/common/Switch";
import { FormValues } from "~/src/components/features/popup/PopupGroupPatternForm/validationSchema";

import {
  POPUP_VIDEO_MAX_SIZE_MB,
  clearUploadedVideoFields,
  defaultPopupVideoFormValues,
} from "./popupVideoFormUtils";
import { VideoUploadField } from "./VideoUploadField";

type Props = {
  role: UserRole;
};

export const PopupVideoFormContainer: FC<
  React.PropsWithChildren<React.PropsWithChildren<Props>>
> = ({ role }) => {
  const { values, setFieldValue, errors, submitCount } =
    useFormikContext<FormValues>();
  const videoSource = values.popupVideo?.videoSource || "YOUTUBE";
  const previewSrc =
    values.popupVideo?.playbackUrl || values.popupVideo?.videoFileUrl;
  const isVideoRequired = values.contentKind === "VIDEO";
  const popupVideoError =
    submitCount > 0 && typeof errors.popupVideo === "string"
      ? errors.popupVideo
      : undefined;

  useEffect(() => {
    if (values.contentKind === "VIDEO" && !values.popupVideo) {
      setFieldValue("popupVideo", defaultPopupVideoFormValues());
    }
  }, [setFieldValue, values.contentKind, values.popupVideo]);

  const handleDeleteUploadedVideo = useCallback(() => {
    clearUploadedVideoFields(setFieldValue);
  }, [setFieldValue]);

  const handleMuteChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setFieldValue("popupVideo.mute", e.target.checked ? 1 : 0);
    },
    [setFieldValue]
  );

  return (
    <>
      <FormControl label="動画の種類">
        <FormikRadioButtonsField
          name="popupVideo.videoSource"
          options={GraphQLEnums.PopupVideoSourceKind}
        />
      </FormControl>

      {videoSource === "YOUTUBE" && (
        <FormControl label="動画のURL (YouTube)">
          <FormikTextField
            name="popupVideo.video"
            type="text"
            mode="fill"
            placeholder="https://www.youtube.com/watch?v=..."
          />
        </FormControl>
      )}

      {videoSource === "UPLOADED" && (
        <FormControl
          required={isVideoRequired}
          label={`MP4ファイル (最大${POPUP_VIDEO_MAX_SIZE_MB}MB)`}
        >
          <VideoUploadField
            name="popupVideo.videoFile"
            previewSrc={previewSrc}
            onDelete={handleDeleteUploadedVideo}
          />
        </FormControl>
      )}

      {videoSource === "UPLOADED" && (
        <FormControl label="ミュートボタンを表示">
          <Switch
            isChecked={values.popupVideo?.mute === 1}
            onChange={handleMuteChange}
          />
        </FormControl>
      )}

      {popupVideoError && (
        <Text color="red.500" fontSize="sm">
          {popupVideoError}
        </Text>
      )}

      {(role === "ADMIN" ||
        role === "SUPER" ||
        role === "ADMIN_ALL" ||
        role === "CLIENT") && (
        <>
          <FormControl label="画像クリック時のURL">
            <FormikTextField
              type="text"
              mode="fill"
              name="popupVideo.linkUrl"
              placeholder="https://"
            />
          </FormControl>
          <FormControl label="画面比率">
            <FormikSelectField
              name="popupVideo.screenType"
              options={GraphQLEnums.PopupVideoScreenKind}
            />
          </FormControl>
        </>
      )}
    </>
  );
};
